import { useEffect, useState } from "react";

import { useAuthContext } from "../auth/AuthContext";
import { jwtUserOid } from "../auth/jwt";
import { getPpToken, resolveIdentityEmail } from "../auth/ppToken";
import { fetchEntitledIpas } from "../worklist/fetchEntitledIpas";
import { getStoredIpa } from "../worklist/ipaStorage";

/**
 * AM-5768 — the ACTING USER's IPA, for scoping @astrana/ui Create Auth's
 * Referring Provider panel (`providerScopeCompanyId`).
 *
 * WHY this is not just `companyId`: PP's `AuthMember/GetAuthSearchProviderList`
 * resolves referring providers from PortalManager's `UserProvider` grants
 * (`WHERE UP.UserObjectId = @UserObjectId AND UP.IPA = @IPA`), so the company
 * it is given selects WHICH SLICE OF THE ACTING USER'S OWN referral panel is
 * offered. Provider Portal passes the acting user's IPA
 * (`useCompanyInfo().ipaGroup`) for exactly this, while keying member lookup on
 * `member.RmiCompanyId` — two separate axes, used side by side in PP's own
 * `Member360Panel/ProviderSelectForCreateAuth`. PP never diverges because it
 * scopes member search to the selected IPA; this host can open ANY member
 * (search / EHR / worklist), so passing the member's tenant scoped the panel to
 * the wrong IPA. Measured in QA: 35 of 38 users holding both ALPHA and APC
 * grants have different-sized panels across the two, up to 31.5x.
 *
 * WHY NOT reuse `useWorklistAccessGate()`: that hook resolves the same value
 * (`resolvedIpaId`) but it POSTs `ProviderOverview/EntitledIpas` whenever it
 * mounts, and the codebase deliberately keeps that POST off routes that don't
 * need it (see components/AdtBell.tsx's LOADING note — it is lazy-imported
 * specifically so the POST doesn't fire from AppHeader for every PP-linked user
 * on every route). Calling it from the member profile would reintroduce exactly
 * that. This hook reuses the same PRIMITIVES instead — `fetchEntitledIpas`,
 * `jwtUserOid`, `getPpToken`, and crucially the same oid-keyed
 * `ipaStorage` — so a provider who already picked an IPA for the worklist gets
 * that same pick here, with no second picker and no duplicated POST on routes
 * that never open Create Auth.
 *
 * Fail-soft by construction: any failure (403 not-entitled, 5xx, network) or an
 * unresolvable multi-IPA case returns `undefined`, which makes @astrana/ui fall
 * back to `companyId` — i.e. exactly today's behavior. Create Auth must never
 * break on this call.
 */
export interface UseActingUserIpaArgs {
  /**
   * Only fetch when Create Auth is actually reachable for this member. Hooks
   * cannot be called conditionally, so this gates the request instead of the
   * hook — keeping the POST off member profiles where it would go unused.
   */
  enabled: boolean;
  /**
   * The viewed member's IPA (the host's `companyId`). Used ONLY as a
   * tie-breaker for a multi-IPA provider with no stored pick — see
   * `resolveActingUserIpa`.
   */
  memberCompanyId?: string;
}

/**
 * Pure resolution order, extracted so it is directly testable (the first
 * attempt at AM-5768 shipped a no-op precisely because nothing asserted which
 * company value won):
 *
 *   1. Exactly one entitled IPA  → use it.
 *   2. Several, and the worklist's persisted oid-keyed pick is still entitled
 *      → use that (shared with the worklist; no second picker).
 *   3. Several, no valid pick, but the VIEWED MEMBER's IPA is one the user is
 *      entitled to → use that. This is the choice the provider would make
 *      anyway (it is the tenant the auth will be written to) and it needs no
 *      UI, so it is preferred over prompting.
 *   4. Otherwise → `undefined`, so @astrana/ui falls back to `companyId`
 *      (today's behavior). A multi-IPA provider who has never used the worklist
 *      AND is viewing a member outside their entitled IPAs lands here; that
 *      case wants a real picker, which is deliberately out of scope for this
 *      bug fix rather than half-built inside it.
 */
export function resolveActingUserIpa(
  ipas: string[],
  storedIpa: string | null,
  memberCompanyId: string | undefined,
): string | undefined {
  if (ipas.length === 0) return undefined;
  if (ipas.length === 1) return ipas[0];
  if (storedIpa && ipas.includes(storedIpa)) return storedIpa;
  if (memberCompanyId && ipas.includes(memberCompanyId)) return memberCompanyId;
  return undefined;
}

export function useActingUserIpa({
  enabled,
  memberCompanyId,
}: UseActingUserIpaArgs): string | undefined {
  const { selectedProvider, accessToken, accountLabel } = useAuthContext();
  const identityEmail = resolveIdentityEmail(accountLabel);
  const oid = jwtUserOid(accessToken) ?? undefined;
  const ppToken = getPpToken(selectedProvider, accessToken, identityEmail);
  const ppApiBaseUrl = `${import.meta.env.VITE_PROVIDER_PORTAL_API_BASE_URL}/api`;

  const [ipas, setIpas] = useState<string[]>([]);

  // Genuine external-system synchronization (a network fetch), which is the one
  // case this repo's anti-useEffect guidance allows — and the same shape
  // useWorklistAccessGate uses for this identical call. There is no query-hook
  // layer for host-side PP-API calls to route it through.
  useEffect(() => {
    if (!enabled || !ppToken) {
      setIpas([]);
      return;
    }
    let cancelled = false;
    void fetchEntitledIpas(ppApiBaseUrl, ppToken)
      .then((entries) => {
        if (!cancelled) setIpas(entries.map((e) => e.ipa));
      })
      .catch(() => {
        // Fail soft — see the hook's header. Never surface or block on this.
        if (!cancelled) setIpas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, ppToken, ppApiBaseUrl]);

  return resolveActingUserIpa(ipas, getStoredIpa(oid), memberCompanyId);
}
